import { useEffect, useState } from "react";
import { hasRole, useApi, useApp } from "../../context/AppContext";
import { ago } from "../../lib/fleet";

// Weight (docs/tasks/35 phase 5, docs/tasks/33 and 36): the sensor board, its
// load-cell channels and the trays they carry, from the weight state the
// cabinet pushes (GET …/weight). Remote actions go through the command queue
// (POST …/weight/actions); calibration and products stay in the capture app,
// where a person can put the reference load on the tray.

function Kv({ k, children }) {
  return (<><dt className="text-slate-500">{k}</dt><dd className="text-xs">{children}</dd></>);
}

function BoardBlock({ w, expectedFw }) {
  const p = w.pico || {};
  const link = p.link || {};
  const watchdogs = p.watchdog_24h;
  return (
    <div className="card">
      <h2 className="mb-2 text-sm font-semibold">Sensor board</h2>
      <dl className="grid grid-cols-[11rem_1fr] gap-y-1 text-sm">
        <Kv k="Board">
          <span className={w.pico_connected ? "text-emerald-700" : "text-rose-700"}>{w.pico_connected ? "connected" : (p.state || "not connected")}</span>
          {p.state && w.pico_connected ? ` · ${p.state}` : ""} · {p.device_id || "—"}{p.board ? ` · ${p.board}` : ""}
        </Kv>
        <Kv k="Firmware">
          <span className="font-mono">{p.fw || "—"}</span>{p.build ? ` (${p.build})` : ""}
          {expectedFw ? (p.fw === expectedFw ? " · as its type expects" : <span className="text-amber-700"> · its type expects {expectedFw}</span>) : ""}
          {p.rescue != null ? ` · rescue level ${p.rescue}` : p.fw ? <span className="text-amber-700"> · no rescue level: a bad image cannot be recovered remotely</span> : ""}
        </Kv>
        <Kv k="Current boot">{p.uptime_s != null ? `up ${(p.uptime_s / 3600).toFixed(1)} h` : "—"} · {p.boot_reason || "—"}{p.boot_reason === "watchdog" ? <span className="text-rose-700"> (a hang: a firmware bug)</span> : null}</Kv>
        {watchdogs != null && <Kv k="Watchdog restarts, 24 h"><span className={watchdogs ? "text-rose-700" : ""}>{watchdogs}</span></Kv>}
        {(p.boots || []).length > 0 && <Kv k="Last boots">{p.boots.slice(-5).map((b) => `${b.reason} ${ago(b.at)}`).join(" · ")}</Kv>}
        {p.packet_rate != null && <Kv k="Packets">{p.packet_rate}/s{p.packet_rate < 8 ? <span className="text-amber-700"> (low)</span> : null}</Kv>}
        {Object.keys(link).length > 0 && <Kv k="Link since start">{Object.entries(link).map(([k, v]) => `${k} ${v}`).join(" · ")}</Kv>}
        {p.recorder_errors ? <Kv k="Recorder errors"><span className="text-amber-700">{p.recorder_errors}</span></Kv> : null}
        <Kv k="Mode">{w.mode === "camera" ? "trays in a camera cabinet" : w.mode === "weight" ? "a weight cabinet" : w.mode || "—"}</Kv>
        <Kv k="Reported">{ago(w.reported_at || w.received_at)}{w.stale ? <span className="text-amber-700"> (stale)</span> : null}</Kv>
      </dl>
    </div>
  );
}

export function WeightTab({ deviceId, health, type }) {
  const api = useApi();
  const { user } = useApp();
  const [w, setW] = useState(undefined);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const may = hasRole(user, "tenant_admin", "device_operator");
  const load = () => api.getDeviceWeight(deviceId).then(setW).catch((e) => setW(e?.status === 404 ? null : { error: String(e.message || e) }));
  useEffect(() => {
    void load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);

  const r = health?.report || {};
  const busyCabinet = r.door?.state === "open" || (r.session && r.session.state !== "closed");

  const act = async (label, body, confirmText) => {
    if (busyCabinet) { setMsg({ bad: true, text: "The door is open or a visit is running: nothing is done to the trays while someone is at the cabinet." }); return; }
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(label); setMsg(null);
    try {
      const { status, data } = await api.postWeightAction(deviceId, body);
      if (status === 200) setMsg({ text: `${label}: done.` });
      else if (status === 202) setMsg({ text: `${label}: queued; the cabinet has not answered yet.` });
      else setMsg({ bad: true, text: `${label}: ${data?.error?.message || data?.message || `refused (${status})`}` });
      await load();
    } catch (e) {
      setMsg({ bad: true, text: `${label}: ${e.message || e}` });
    } finally {
      setBusy(null);
    }
  };

  if (w === undefined) return <p className="text-sm text-slate-500">Loading…</p>;
  if (w === null) {
    return (
      <p className="text-sm text-slate-500">
        This cabinet has never reported a weight board.{type?.weighed_trays ? <span className="text-amber-700"> Its type has weighed trays, so the board or its service is missing.</span> : ""}
      </p>
    );
  }
  if (w.error) return <p className="text-sm text-rose-700">{w.error}</p>;

  const trays = (w.planogram?.trays || []).filter((t) => (t.channels || []).length || t.product);
  const emptyTrays = (w.planogram?.trays || []).length - trays.length;
  return (
    <div className="space-y-4">
      <BoardBlock w={w} expectedFw={type?.expected?.pico_fw} />

      <div className="card overflow-x-auto">
        <h2 className="mb-2 text-sm font-semibold">Load-cell channels</h2>
        <table className="table">
          <thead><tr><th>Channel</th><th>Answers</th><th>Grams now</th><th>Tray</th><th>Tared</th><th>Calibrated</th><th>Errors, 24 h</th><th>Zero drift</th></tr></thead>
          <tbody>
            {(w.channels || []).map((c) => (
              <tr key={c.channel}>
                <td className="text-xs">{c.channel} · {c.id}</td>
                <td className={`text-xs ${c.connected ? "text-emerald-700" : "text-slate-400"}`}>{c.connected ? "yes" : "no"}{c.error_now ? <span className="text-rose-700"> · {c.error_now}</span> : null}</td>
                <td className="font-mono text-xs">{c.grams != null ? c.grams.toFixed(1) : "—"}</td>
                <td className="text-xs">{c.tray || "—"}</td>
                <td className="text-xs">{c.tared ? `yes${c.tared_at ? ` · ${ago(c.tared_at)}` : ""}` : "no"}</td>
                <td className="text-xs">{c.calibrated ? `yes${c.calibrated_at ? ` · ${ago(c.calibrated_at)}` : ""}` : "no"}</td>
                <td className="text-xs">{c.errors_24h ? Object.entries(c.errors_24h).map(([k, v]) => `${k.replace("HX711_", "")} ${v}`).join(" · ") || "none" : "—"}</td>
                <td className="text-xs">{c.zero_drift_g != null ? `${c.zero_drift_g > 0 ? "+" : ""}${c.zero_drift_g} g` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card overflow-x-auto">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">Trays</h2>
            <p className="text-xs text-slate-500">{emptyTrays} more tray position(s) without a sensor or product. Products, unit weights and calibration are set in the capture app.</p>
          </div>
          {may && (
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-secondary text-xs" disabled={!!busy} onClick={() => act("Recount", { action: "inventory" })}>Recount</button>
              <button className="btn btn-secondary text-xs" disabled={!!busy}
                onClick={() => act("Tare all", { action: "tare_all" }, "Zero every tray now? Every tray must be EMPTY, and the cabinet at its working temperature (a cooling cabinet moves the zero by tens of grams).")}>Tare all</button>
              <button className="btn btn-secondary text-xs" disabled={!!busy}
                onClick={() => act("Restart", { action: "restart" }, "Restart the weight service on the cabinet? It looks for the board for 60 s after it starts.")}>Restart weight service</button>
            </div>
          )}
        </div>
        {trays.length === 0 ? <p className="text-sm text-slate-500">No tray has a sensor or a product.</p> : (
          <table className="table">
            <thead><tr><th>Tray</th><th>Product</th><th>Weight</th><th>Count</th><th>State</th><th /></tr></thead>
            <tbody>
              {trays.map((t) => (
                <tr key={t.id}>
                  <td className="text-xs">{t.id}{(t.lanes || []).length > 1 ? ` (lanes ${t.lanes.join("+")})` : ""} · ch {(t.channels || []).join(", ") || "—"}</td>
                  <td className="text-xs">{t.product ? `${t.product.name || t.product.sku}${t.product.unit_g ? ` · ${t.product.unit_g} g each` : ""}` : "—"}</td>
                  <td className="font-mono text-xs">{t.weight_g != null ? `${t.weight_g.toFixed(1)} g` : "—"}{t.stable ? "" : " ~"}</td>
                  <td className="text-xs">{t.quantity ? `${t.quantity.count}${t.quantity.certain ? "" : " (uncertain)"}` : "—"}{t.max_qty ? ` / ${t.max_qty}` : ""}</td>
                  <td className="text-xs">{t.tared ? "tared" : "not tared"} · {t.calibrated ? "calibrated" : t.approximate ? "datasheet" : "not calibrated"}</td>
                  <td>{may && <button className="text-xs text-sky-700 hover:underline" disabled={!!busy}
                    onClick={() => act(`Tare ${t.id}`, { action: "tare_tray", tray: t.id }, `Zero tray ${t.id} now? It must be EMPTY.`)}>tare</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {busy && <p className="mt-2 text-xs text-slate-500">{busy}: waiting for the cabinet…</p>}
        {msg && <p className={`mt-2 text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</p>}
      </div>
    </div>
  );
}
